import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing Supabase environment variables. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY before building.'
  );
}

export const SUPABASE_URL = supabaseUrl;
export const SUPABASE_ANON_KEY = supabaseAnonKey;

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
  },
});

export type SessionState = 'idea_selection' | 'idea_approved' | 'article_generated' | 'article_approved' | 'image_selected' | 'completed';
export type IdeaSource = 'zapier_initial' | 'ai_regenerated' | 'user_custom';
export type IdeaStatus = 'presented' | 'approved' | 'rejected';

export interface User {
  id: string;
  email: string;
  first_name: string;
  company_name: string;
  user_profile: string;
  website_url: string;
  image_preferences: string;
  username_slug: string | null;
  blog_schedule_enabled?: boolean;
  blog_schedule_frequency?: string;
  blog_schedule_days_of_week?: number[];
  blog_schedule_time?: string;
  blog_schedule_timezone?: string;
  sendgrid_domain_id?: string | null;
  sendgrid_domain_name?: string | null;
  sendgrid_domain_verified?: boolean;
  sendgrid_dns_records?: any;
  sendgrid_custom_dkim_selector?: string;
  created_at: string;
  updated_at: string;
}

export interface Session {
  id: string;
  user_id: string;
  state: SessionState;
  approved_idea_id: string | null;
  article_content: string | null;
  article_last_edited: string | null;
  selected_title: string | null;
  selected_intro: string | null;
  selected_image_url: string | null;
  article_direction?: string | null;
  article_focus?: string | null;
  image_generation_count?: number | null;
  titles?: any;
  intros?: any;
  images?: any;
  reminder_count: number;
  created_at: string;
  expires_at: string;
  last_activity: string;
}

export interface BlogIdea {
  id: string;
  session_id: string;
  user_id: string;
  source: IdeaSource;
  idea_title: string;
  idea_content: string;
  idea_reason: string;
  status: IdeaStatus;
  rejection_reason: string | null;
  presented_at: string;
  decided_at: string | null;
}

export type BlogStatus = 'published' | 'archived';

export interface PublishedBlog {
  id: string;
  user_id: string;
  session_id: string;
  slug: string;
  title: string;
  intro: string;
  content: string;
  featured_image_url: string | null;
  status: BlogStatus;
  view_count: number;
  share_count: number;
  published_at: string;
  updated_at: string;
  created_at: string;
}

export interface BlogData {
  id: string;
  user_id: string;
  session_id: string;
  slug: string;
  title: string;
  intro: string;
  content: string;
  featured_image_url: string | null;
  status: BlogStatus;
  view_count: number;
  share_count: number;
  published_at: string;
  updated_at: string;
  created_at: string;
  user?: {
    first_name: string;
    company_name: string;
    username_slug: string | null;
  };
}

export interface BlogSEOMetadata {
  id: string;
  blog_id: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  og_title: string;
  og_description: string;
  og_image: string | null;
  twitter_card: string;
  twitter_title: string;
  twitter_description: string;
  twitter_image: string | null;
  structured_data: Record<string, any>;
  canonical_url: string;
  created_at: string;
  updated_at: string;
}

export interface BlogShareLink {
  share_id: string;
  blog_id: string;
  user_id: string;
  click_count: number;
  last_clicked_at: string | null;
  created_at: string;
  updated_at: string;
}
